import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useUserPreferences } from '../../user-preferences';
import type { ColorMode, Iconography, SectionIds } from '../types';

import { useControlledState } from './hooks/use-controlled-state';
import FormSection from './sections';
import ColorModeSection from './sections/color-mode';
import Footer from './sections/footer';
import VisualRefresh from './sections/visual-refresh';

const styles = cssMap({
	container: {
		paddingBlockStart: token('space.250'),
		paddingInlineEnd: token('space.250'),
		paddingBlockEnd: token('space.250'),
		paddingInlineStart: token('space.250'),
	},
	footer: {
		paddingBlockStart: token('space.250'),
		paddingInlineEnd: token('space.250'),
		paddingBlockEnd: token('space.250'),
		paddingInlineStart: token('space.250'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

type AppearanceControlsFormProps = {
	colorMode?: ColorMode;
	iconography?: Iconography;
	isSynced?: boolean;
	onColorModeChange?(colorMode: ColorMode): void;
	onIconographyChange?(iconography: Iconography): void;
	onSyncedChange?(isSynced: boolean): void;
	testId?: string;
	hiddenSections?: SectionIds;
};

/**
 * __Appearance controls form__
 *
 * The form containing fields for example appearance controls.
 */
const AppearanceControlsForm = ({
	colorMode: controlledColorMode,
	iconography: controlledIconography,
	isSynced: controlledIsSynced,
	onColorModeChange,
	onIconographyChange,
	onSyncedChange,
	testId,
	hiddenSections,
}: AppearanceControlsFormProps) => {
	const { userPreferences, setUserPreferences } = useUserPreferences();

	const [uncontrolledIsSynced, setUncontrolledIsSynced] = useState<boolean>(
		userPreferences['exampleSyncSettings'] === 'true' || false,
	);
	const isSynced = controlledIsSynced ?? uncontrolledIsSynced;

	const [colorMode, handleColorModeChange] = useControlledState({
		controlledValue: controlledColorMode,
		defaultValue: 'auto',
		preferenceKey: 'exampleColorMode',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onColorModeChange,
	});

	const [iconography, handleIconographyChange] = useControlledState({
		controlledValue: controlledIconography,
		defaultValue: 'new',
		preferenceKey: 'exampleIconography',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onIconographyChange,
	});

	const handleSyncedChange = useCallback(
		(isSynced: boolean) => {
			onSyncedChange?.(isSynced);
			if (!controlledIsSynced) {
				setUncontrolledIsSynced(isSynced);
			}

			if (isSynced) {
				setUserPreferences({
					exampleSyncSettings: 'true',
					exampleColorMode: colorMode,
					exampleIconography: iconography,
				});
			} else {
				// Wipe example user preferences
				setUserPreferences({
					exampleSyncSettings: 'false',
					exampleColorMode: null,
					exampleIconography: null,
				});
			}
		},
		[onSyncedChange, controlledIsSynced, setUserPreferences, colorMode, iconography],
	);

	return (
		<Stack testId={testId}>
			<Stack space="space.250" xcss={styles.container}>
				<FormSection>
					<ColorModeSection
						colorMode={colorMode}
						onColorModeChange={handleColorModeChange}
						testId={testId ? `${testId}--color-mode-section` : undefined}
					/>
				</FormSection>
				<FormSection>
					<VisualRefresh
						iconography={iconography}
						onIconographyChange={handleIconographyChange}
						testId={testId ? `${testId}--visual-refresh-section` : undefined}
						hiddenSections={hiddenSections}
					/>
				</FormSection>
			</Stack>
			<Box xcss={styles.footer}>
				<FormSection>
					<Footer
						isSynced={isSynced}
						onSyncedChange={handleSyncedChange}
						testId={testId ? `${testId}--footer` : undefined}
					/>
				</FormSection>
			</Box>
		</Stack>
	);
};

export default AppearanceControlsForm;
